import { createApi } from '@reduxjs/toolkit/query/react';

import { AdminNotifications } from '../endpoints';
import {
  getBaseQuery,
  TransformedErrorResponseWithNotification,
  TransformedResponseWithNotification,
} from '../generic';
import { HTTP_METHOD, REDUX_CACHE } from '../constants';
import type {
  IBulkDispatchBody,
  IBulkDispatchResponse,
  ICreateNotificationCampaignBody,
  ICreateNotificationTemplateBody,
  ICreateNotificationTranslationBody,
  INotificationCampaign,
  INotificationPreviewRequest,
  INotificationPreviewResponse,
  INotificationTemplate,
  INotificationTemplateTranslation,
  IPatchNotificationCampaignBody,
  IPatchNotificationTemplateBody,
  IPatchNotificationTranslationBody,
} from '../../../types/interfaces/notification-admin';

const TEMPLATE = 'NotificationTemplate' as const;
const TRANSLATION = 'NotificationTranslation' as const;
const CAMPAIGN = 'NotificationCampaign' as const;

function templateTags(result: INotificationTemplate[] | undefined) {
  return result
    ? [
        ...result.map((item) => ({ type: TEMPLATE, id: item.id })),
        { type: TEMPLATE, id: REDUX_CACHE.LIST },
      ]
    : [{ type: TEMPLATE, id: REDUX_CACHE.LIST }];
}

function campaignTags(result: INotificationCampaign[] | undefined) {
  return result
    ? [
        ...result.map((item) => ({ type: CAMPAIGN, id: item.id })),
        { type: CAMPAIGN, id: REDUX_CACHE.LIST },
      ]
    : [{ type: CAMPAIGN, id: REDUX_CACHE.LIST }];
}

export const notificationsAdminApi = createApi({
  reducerPath: 'notificationsAdminApi',
  baseQuery: getBaseQuery(),
  tagTypes: [TEMPLATE, TRANSLATION, CAMPAIGN],
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getNotificationTemplates: builder.query<INotificationTemplate[], void>({
      query() {
        return { url: AdminNotifications.TEMPLATES, method: HTTP_METHOD.GET };
      },
      providesTags: (result) => templateTags(result),
      ...TransformedErrorResponseWithNotification(),
    }),
    getNotificationTemplateById: builder.query<INotificationTemplate, string>({
      query(id) {
        return {
          url: AdminNotifications.TEMPLATE_BY_ID(id),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (_r, _e, id) => [{ type: TEMPLATE, id }],
      ...TransformedErrorResponseWithNotification(),
    }),
    createNotificationTemplate: builder.mutation<
      INotificationTemplate,
      ICreateNotificationTemplateBody
    >({
      query(body) {
        return {
          url: AdminNotifications.TEMPLATES,
          method: HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: [{ type: TEMPLATE, id: REDUX_CACHE.LIST }],
      ...TransformedResponseWithNotification('Notification template'),
    }),
    patchNotificationTemplate: builder.mutation<
      INotificationTemplate,
      { id: string; body: IPatchNotificationTemplateBody }
    >({
      query({ id, body }) {
        return {
          url: AdminNotifications.TEMPLATE_BY_ID(id),
          method: HTTP_METHOD.PATCH,
          body,
        };
      },
      invalidatesTags: (_r, _e, { id }) => [
        { type: TEMPLATE, id },
        { type: TEMPLATE, id: REDUX_CACHE.LIST },
      ],
      ...TransformedResponseWithNotification('Notification template'),
    }),
    deleteNotificationTemplate: builder.mutation<void, string>({
      query(id) {
        return {
          url: AdminNotifications.TEMPLATE_BY_ID(id),
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (_r, _e, id) => [
        { type: TEMPLATE, id },
        { type: TEMPLATE, id: REDUX_CACHE.LIST },
        { type: TRANSLATION, id },
      ],
      ...TransformedResponseWithNotification('Notification template'),
    }),
    getNotificationTranslations: builder.query<
      INotificationTemplateTranslation[],
      string
    >({
      query(templateId) {
        return {
          url: AdminNotifications.TRANSLATIONS(templateId),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (_r, _e, templateId) => [
        { type: TRANSLATION, id: templateId },
      ],
      ...TransformedErrorResponseWithNotification(),
    }),
    createNotificationTranslation: builder.mutation<
      INotificationTemplateTranslation,
      { templateId: string; body: ICreateNotificationTranslationBody }
    >({
      query({ templateId, body }) {
        return {
          url: AdminNotifications.TRANSLATIONS(templateId),
          method: HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (_r, _e, { templateId }) => [
        { type: TRANSLATION, id: templateId },
        { type: TEMPLATE, id: templateId },
      ],
      ...TransformedResponseWithNotification('Translation'),
    }),
    patchNotificationTranslation: builder.mutation<
      INotificationTemplateTranslation,
      {
        templateId: string;
        translationId: string;
        body: IPatchNotificationTranslationBody;
      }
    >({
      query({ templateId, translationId, body }) {
        return {
          url: AdminNotifications.TRANSLATION_BY_ID(templateId, translationId),
          method: HTTP_METHOD.PATCH,
          body,
        };
      },
      invalidatesTags: (_r, _e, { templateId }) => [
        { type: TRANSLATION, id: templateId },
        { type: TEMPLATE, id: templateId },
      ],
      ...TransformedResponseWithNotification('Translation'),
    }),
    deleteNotificationTranslation: builder.mutation<
      void,
      { templateId: string; translationId: string }
    >({
      query({ templateId, translationId }) {
        return {
          url: AdminNotifications.TRANSLATION_BY_ID(templateId, translationId),
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (_r, _e, { templateId }) => [
        { type: TRANSLATION, id: templateId },
        { type: TEMPLATE, id: templateId },
      ],
      ...TransformedResponseWithNotification('Translation'),
    }),
    previewNotification: builder.mutation<
      INotificationPreviewResponse,
      INotificationPreviewRequest
    >({
      query(body) {
        return {
          url: AdminNotifications.PREVIEW,
          method: HTTP_METHOD.POST,
          body,
        };
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    getNotificationCampaigns: builder.query<INotificationCampaign[], void>({
      query() {
        return { url: AdminNotifications.CAMPAIGNS, method: HTTP_METHOD.GET };
      },
      providesTags: (result) => campaignTags(result),
      ...TransformedErrorResponseWithNotification(),
    }),
    getNotificationCampaignById: builder.query<INotificationCampaign, string>({
      query(id) {
        return {
          url: AdminNotifications.CAMPAIGN_BY_ID(id),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (_r, _e, id) => [{ type: CAMPAIGN, id }],
      ...TransformedErrorResponseWithNotification(),
    }),
    createNotificationCampaign: builder.mutation<
      INotificationCampaign,
      ICreateNotificationCampaignBody
    >({
      query(body) {
        return {
          url: AdminNotifications.CAMPAIGNS,
          method: HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: [{ type: CAMPAIGN, id: REDUX_CACHE.LIST }],
      ...TransformedResponseWithNotification('Campaign'),
    }),
    patchNotificationCampaign: builder.mutation<
      INotificationCampaign,
      { id: string; body: IPatchNotificationCampaignBody }
    >({
      query({ id, body }) {
        return {
          url: AdminNotifications.CAMPAIGN_BY_ID(id),
          method: HTTP_METHOD.PATCH,
          body,
        };
      },
      invalidatesTags: (_r, _e, { id }) => [
        { type: CAMPAIGN, id },
        { type: CAMPAIGN, id: REDUX_CACHE.LIST },
      ],
      ...TransformedResponseWithNotification('Campaign'),
    }),
    deleteNotificationCampaign: builder.mutation<void, string>({
      query(id) {
        return {
          url: AdminNotifications.CAMPAIGN_BY_ID(id),
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (_r, _e, id) => [
        { type: CAMPAIGN, id },
        { type: CAMPAIGN, id: REDUX_CACHE.LIST },
      ],
      ...TransformedResponseWithNotification('Campaign'),
    }),
    dispatchBulkNotifications: builder.mutation<
      IBulkDispatchResponse,
      IBulkDispatchBody
    >({
      query(body) {
        return {
          url: AdminNotifications.DISPATCH_BULK,
          method: HTTP_METHOD.POST,
          body,
        };
      },
      ...TransformedErrorResponseWithNotification(),
    }),
  }),
});

export const {
  useGetNotificationTemplatesQuery,
  useGetNotificationTemplateByIdQuery,
  useCreateNotificationTemplateMutation,
  usePatchNotificationTemplateMutation,
  useDeleteNotificationTemplateMutation,
  useGetNotificationTranslationsQuery,
  useCreateNotificationTranslationMutation,
  usePatchNotificationTranslationMutation,
  useDeleteNotificationTranslationMutation,
  usePreviewNotificationMutation,
  useGetNotificationCampaignsQuery,
  useGetNotificationCampaignByIdQuery,
  useCreateNotificationCampaignMutation,
  usePatchNotificationCampaignMutation,
  useDeleteNotificationCampaignMutation,
  useDispatchBulkNotificationsMutation,
} = notificationsAdminApi;
